import { addDays, todayStr } from "../domain/date";
import { dinnerNudge } from "../domain/dinnerNudge";
import { sessionKeyFor, typeName } from "../domain/plan";
import type { KayaState } from "../domain/types";
import { remainingNudges, toMin } from "../domain/water";
import { canNotify, cancelNotification, scheduleNotification } from "./device";
import { pushEndpoint } from "./push";

/**
 * Re-arms the two daily reminders.
 *
 * Web apps cannot run alarms in the background, so these only fire if Kaya was opened
 * within the previous day — which is why this runs on every launch as well as on save.
 * The Profile screen says as much and offers a calendar export for a guaranteed nudge.
 */
export function scheduleReminders(S: KayaState): void {
  cancelNotification("rm-m");
  cancelNotification("rm-e");
  scheduleWaterReminders(S);
  scheduleDinnerReminder(S);
  if (!S.profile.remindOn || !canNotify()) return;

  const at = (t: string, id: string, title: string, body: (ds: string) => string) => {
    const [hh, mm] = t.split(":").map(Number);
    const d = new Date();
    d.setHours(hh, mm, 0, 0);
    if (d <= new Date()) d.setDate(d.getDate() + 1);
    // Sunday is the rest day; push it to Monday rather than nagging on a day off
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    scheduleNotification(id, d.getTime(), title, body(todayStr(d)));
  };

  at(
    S.profile.remindMorning,
    "rm-m",
    "Kaya — morning session",
    (ds) => "Today: " + typeName(sessionKeyFor(S, ds)) + ". Start with the warm-up."
  );
  at(
    S.profile.remindEvening,
    "rm-e",
    "Kaya — treadmill time",
    () => "30 minutes of intervals, then two stretches."
  );
}

/**
 * "Log dinner?" as a service-worker timer — the same best-effort path as water's, until the
 * server's push carries it. Re-run on every change to the state, so logging a dinner cancels
 * tonight's; once tonight's time has passed, tomorrow's is armed instead and re-asked the next
 * time the app runs.
 */
function scheduleDinnerReminder(S: KayaState): void {
  cancelNotification("dn");
  if (!canNotify()) return;
  const now = new Date();
  const today = todayStr(now);
  for (const [i, ds] of [today, addDays(today, 1)].entries()) {
    const n = dinnerNudge(S, ds);
    if (!n) continue;
    const [hh, mm] = n.at.split(":").map(Number);
    const d = new Date(now);
    d.setDate(d.getDate() + i);
    d.setHours(hh, mm, 0, 0);
    if (d <= now) continue;
    scheduleNotification("dn", d.getTime(), n.title, n.body);
    return;
  }
}

/** Enough ids for a reminder every hour of the day; each run clears them all before re-arming. */
const WATER_IDS = 24;

/**
 * Today's remaining water reminders as service-worker timers — best effort only, while the app or
 * its worker happens to be alive. The delivery path is the server's push (ADR-044, ADR-045), and
 * a device subscribed to it gets no timers at all: the same slot would otherwise arrive twice.
 * Re-run on every change to the state, so a drink logged since is already reflected: the
 * skip-if-logged and stop-at-target checks cannot run inside the worker, which cannot read state.
 */
function scheduleWaterReminders(S: KayaState): void {
  for (let i = 0; i < WATER_IDS; i++) cancelNotification(`wt-${i}`);
  if (!canNotify() || pushEndpoint()) return;
  const now = new Date();
  const ds = todayStr(now);
  remainingNudges(S, ds, now.getHours() * 60 + now.getMinutes())
    .slice(0, WATER_IDS)
    .forEach((n, i) => {
      const d = new Date(now);
      const m = toMin(n.slot);
      d.setHours(Math.floor(m / 60), m % 60, 0, 0);
      scheduleNotification(`wt-${i}`, d.getTime(), n.title, n.body);
    });
}
